/*
 * vld-editor.js - the VickersLDN customiser (Studio in the hub, product page on Shopify). Needs vld-design.js.
 *
 *   const ed = await VLDEditor.mount(rootEl, {hub: "https://<hub>", designId: 100, base: "34", allFonts: false});
 *   // allFonts: true (hub Studio) lists the whole catalogue; customers get the curated font chips
 *   ed.state()            -> {design_id, design_version, overrides, product: {base, backdrop, colour}, warnings}
 *   await ed.previewBlob() -> PNG of the engraved product (for the cart / order)
 *
 * Everything runs in the browser: the design is drawn as SVG (vld-design.js), rasterised with its fonts and art
 * embedded, and burnt onto the product photo with the same maths as services/engrave.py (keep in step).
 */
(function (root) {
  const EDGE_BLUR = 0.6, RIM = 1, SHADOW = 0.65, HIGHLIGHT = 0.45; // services/engrave.py
  const dataCache = {};

  async function asDataUrl(url) {
    if (!dataCache[url]) dataCache[url] = fetch(url, {mode: "cors", cache: "no-cache"}).then(r => {
      if (!r.ok) throw new Error(url + " " + r.status);
      return r.blob();
    }).then(b => new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }));
    return dataCache[url];
  }

  function h(tag, attrs, html) {
    const e = document.createElement(tag);
    for (const k in attrs || {}) {
      if (k === "on") for (const ev in attrs.on) e.addEventListener(ev, attrs.on[ev]);
      else if (attrs[k] !== undefined && attrs[k] !== null) e.setAttribute(k, attrs[k]);
    }
    if (html !== undefined) e.innerHTML = html;
    return e;
  }
  const esc = s => String(s ?? "").replace(/[&<>"]/g, c => ({"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;"}[c]));

  async function selfContainedSvg(svgEl, fontsJsonUrl, fontsBase) {
    // copy of the SVG with every image and used font embedded, so it can be drawn into a canvas
    const clone = svgEl.cloneNode(true);
    const imgs = clone.querySelectorAll("image");
    await Promise.all([...imgs].map(async im => {
      const href = im.getAttribute("href") || im.getAttributeNS("http://www.w3.org/1999/xlink", "href");
      if (!href || href.startsWith("data:")) return;
      let d;
      try {
        d = await asDataUrl(href);
      } catch (e) {
        const fb = im.getAttribute("data-fallback");   // vector missing: use the layer's PNG (vld-design.js sets it)
        if (!fb) throw e;
        d = await asDataUrl(fb);
      }
      im.setAttribute("href", d);
      im.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", d);
    }));
    const families = new Set([...clone.querySelectorAll("[font-family]")].map(e => e.getAttribute("font-family")));
    const cat = await VLD.fonts(fontsJsonUrl);
    let css = "";
    for (const fam of families) {
      const id = fam.replace(/^vld-/, ""), f = cat.fonts[id];
      if (!f) continue;
      css += `@font-face{font-family:'${fam}';src:url(${await asDataUrl(fontsBase + f.file)});font-weight:100 900}`;
    }
    const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
    style.textContent = css;
    clone.insertBefore(style, clone.firstChild);
    clone.setAttribute("width", svgEl.viewBox.baseVal.width);
    clone.setAttribute("height", svgEl.viewBox.baseVal.height);
    return new XMLSerializer().serializeToString(clone);
  }

  function loadImage(src) {
    return new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = rej; im.src = src; });
  }

  function blurred(mask, blur, dx, dy) {
    const c = new OffscreenCanvas(mask.width, mask.height), x = c.getContext("2d");
    x.filter = `blur(${blur}px)`; x.drawImage(mask, dx, dy);
    return x.getImageData(0, 0, c.width, c.height).data;
  }

  function burnLook(plate, mask, look, unit) {
    // Learned PSD look (services/engrave_look.py, keep in step): log(out / plate) per channel = weights x a filter bank
    // on the mask (blurs at several radii; a 1.5-blur shifted towards / away from the light, alone and times the mask;
    // mask x blur5, mask x blur10). Radii are in PSD pixels; `unit` = plate pixels per PSD pixel.
    const W = plate.naturalWidth || plate.width, H = plate.naturalHeight || plate.height;
    const out = new OffscreenCanvas(W, H), ctx = out.getContext("2d");
    ctx.drawImage(plate, 0, 0);
    const img = ctx.getImageData(0, 0, W, H), p = img.data;
    const m = blurred(mask, 0, 0, 0);
    const bl = look.radii.map(r => r ? blurred(mask, r * unit, 0, 0) : m);
    const i5 = look.radii.indexOf(5), i10 = look.radii.indexOf(10);
    const sh = look.shifts.map(([dx, dy]) => blurred(mask, 1.5 * unit, Math.round(dx * unit), Math.round(dy * unit)));
    const wt = look.weights;           // [3][nFeatures]; feature 0 is the constant (no effect without a design)
    const f = new Float32Array(wt[0].length);
    for (let i = 0; i < p.length; i += 4) {
      let any = bl[bl.length - 1][i + 3];
      for (let k = 0; k < sh.length && !any; k++) any = sh[k][i + 3];
      if (!any) continue;
      const mv = m[i + 3] / 255;
      let n = 1;
      for (let k = 0; k < bl.length; k++) f[n++] = bl[k][i + 3] / 255;
      for (let k = 0; k < sh.length; k++) { const v = sh[k][i + 3] / 255; f[n++] = v; f[n++] = v * mv; }
      f[n++] = mv * bl[i5][i + 3] / 255; f[n++] = mv * bl[i10][i + 3] / 255;
      for (let c = 0; c < 3; c++) {
        let lr = 0; const w = wt[c];
        for (let k = 1; k < n; k++) lr += w[k] * f[k];
        p[i + c] = Math.min(255, p[i + c] * Math.exp(Math.max(-3, Math.min(1.5, lr))));
      }
    }
    ctx.putImageData(img, 0, 0);
    return out;
  }

  function burn(plate, mask, rec) {
    // mask: canvas the size of the plate, white = engrave
    if (rec.look) return burnLook(plate, mask, rec.look, rec.look_unit || 1);
    const W = plate.naturalWidth || plate.width, H = plate.naturalHeight || plate.height;
    const out = new OffscreenCanvas(W, H), ctx = out.getContext("2d");
    ctx.drawImage(plate, 0, 0);
    const img = ctx.getImageData(0, 0, W, H), p = img.data;
    const a = blurred(mask, EDGE_BLUR, 0, 0), sh = blurred(mask, RIM, RIM, RIM), hl = blurred(mask, RIM, -RIM, -RIM);
    const [kr, kg, kb] = rec.burn;
    for (let i = 0; i < p.length; i += 4) {
      const al = a[i + 3] / 255, rd = Math.max(0, sh[i + 3] / 255 - al), rl = Math.max(0, hl[i + 3] / 255 - al);
      if (al === 0 && rd === 0 && rl === 0) continue;
      for (let c = 0; c < 3; c++) {
        const v = p[i + c], k = c === 0 ? kr : c === 1 ? kg : kb;
        let o = v * (1 - al) + Math.min(255, v * k) * al;
        o = o * (1 - SHADOW * rd);
        o = o + (255 - o) * HIGHLIGHT * rl;
        p[i + c] = o;
      }
    }
    ctx.putImageData(img, 0, 0);
    return out;
  }

  async function artworkMask(svgString, W, H, rec, fill, referenceBounds) {
    if(referenceBounds){
      const [x0,y0,x1,y1]=referenceBounds,[bx0,by0,bx1,by1]=rec.bbox;
      const s=Math.min((bx1-bx0)*.62*fill/.86/(x1-x0+1),(by1-by0)*fill/(y1-y0+1));
      const dx=(bx0+bx1)/2-(x1-x0+1)*s/2,dy=(by0+by1)/2-(y1-y0+1)*s/2;
      const doc=new DOMParser().parseFromString(svgString,'image/svg+xml'),node=doc.documentElement;
      // Rasterise the full plate window in design coordinates, so a moved layer
      // is not clipped by the original design's smaller SVG viewBox.
      node.setAttribute('viewBox',[x0-dx/s,y0-dy/s,W/s,H/s].join(' '));node.setAttribute('width',W);node.setAttribute('height',H);
      const img=await loadImage('data:image/svg+xml;charset=utf-8,'+encodeURIComponent(new XMLSerializer().serializeToString(node)));
      const mask=new OffscreenCanvas(W,H),ctx=mask.getContext('2d');ctx.drawImage(img,0,0,W,H);
      const pixels=ctx.getImageData(0,0,W,H),q=pixels.data;
      for(let i=0;i<q.length;i+=4){const a=(255-(q[i]*.299+q[i+1]*.587+q[i+2]*.114))*q[i+3]/255;q[i]=q[i+1]=q[i+2]=255;q[i+3]=a;}
      ctx.putImageData(pixels,0,0);return {mask,fit:{x0,y0,s,dx,dy,W,H},bounds:referenceBounds};
    }
    // rasterise the artwork, turn dark ink into a white mask, and fit it into the product's engraving area.
    // Returns {mask, fit}: fit maps plate pixels back to artwork (viewBox) units, for taps on the engraved photo.
    const img = await loadImage("data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString));
    const a = new OffscreenCanvas(img.width || 1200, img.height || 800), ax = a.getContext("2d");
    ax.drawImage(img, 0, 0);
    const d = ax.getImageData(0, 0, a.width, a.height), q = d.data;
    let x0 = a.width, y0 = a.height, x1 = 0, y1 = 0;
    for (let i = 0, n = 0; i < q.length; i += 4, n++) {
      const lum = (q[i] * 0.299 + q[i + 1] * 0.587 + q[i + 2] * 0.114);
      const v = (255 - lum) * q[i + 3] / 255;
      q[i] = q[i + 1] = q[i + 2] = 255; q[i + 3] = v;
      if (v > 20) { const x = n % a.width, y = (n / a.width) | 0; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    ax.putImageData(d, 0, 0);
    const m = new OffscreenCanvas(W, H), mx = m.getContext("2d");
    const bounds = referenceBounds || [x0, y0, x1, y1];
    [x0, y0, x1, y1] = bounds;
    if (x1 <= x0) return {mask: m, fit: null, bounds: null};
    const [bx0, by0, bx1, by1] = rec.bbox;
    const bw = (bx1 - bx0) * 0.62 * fill / 0.86, bh = (by1 - by0) * fill; // services/factory.engrave_design
    const s = Math.min(bw / (x1 - x0 + 1), bh / (y1 - y0 + 1));
    const w = (x1 - x0 + 1) * s, hh = (y1 - y0 + 1) * s;
    const dx = (bx0 + bx1) / 2 - w / 2, dy = (by0 + by1) / 2 - hh / 2;
    // Keep the original design-to-product transform. Cropping to the current ink
    // bounds would resize/recentre every other layer when one layer changes.
    if (referenceBounds) mx.drawImage(a, dx - x0 * s, dy - y0 * s, a.width * s, a.height * s);
    else mx.drawImage(a, x0, y0, x1 - x0 + 1, y1 - y0 + 1, dx, dy, w, hh);
    return {mask: m, fit: {x0, y0, s, dx, dy, W, H}, bounds};
  }

  async function engraveText(plate, rec, o) {
    // One or two lines of plain text (inside / back engraving) burnt into a product photo with the same maths as
    // the front. plate: loaded <img> (or canvas); rec: {bbox, burn}; o: {text, font: "id[:weight]", fill}.
    // If the photo is cross-origin without CORS the pixels cannot be read: the text is then drawn as plain dark ink.
    const W = plate.naturalWidth || plate.width, H = plate.naturalHeight || plate.height;
    const f = VLD.resolveFont(o.font || "great-vibes");
    const lines = String(o.text || "").replace(/\r/g, "").split("\n").map(s => s.trim()).filter(Boolean).slice(0, 2);
    const out = new OffscreenCanvas(W, H), octx = out.getContext("2d");
    octx.drawImage(plate, 0, 0, W, H);
    if (!lines.length) return out;
    const probe = `${f.weight} 100px "${f.family}"`;
    if (!document.fonts || !(await document.fonts.load(probe, lines.join(""))).length) {
      throw new Error("The selected engraving font could not load");
    }
    const [bx0, by0, bx1, by1] = rec.bbox, fill = Math.max(0.3, Math.min(0.8, Number(o.fill) || 0.8));
    const mask = new OffscreenCanvas(W, H), mx = mask.getContext("2d");
    mx.font = probe; mx.textAlign = "center"; mx.textBaseline = "alphabetic";
    const ms = lines.map(t => mx.measureText(t));
    const asc = Math.max(...ms.map(m => m.actualBoundingBoxAscent || 70)), desc = Math.max(...ms.map(m => m.actualBoundingBoxDescent || 25));
    const lineH = asc + desc, gap = lines.length > 1 ? 20 : 0;
    const wMax = Math.max(...ms.map(m => m.width)), hAll = lineH * lines.length + gap * (lines.length - 1);
    const s = Math.min((bx1 - bx0) * fill / Math.max(wMax, 1), (by1 - by0) * fill / Math.max(hAll, 1));
    const marginX = (bx1 - bx0) * 0.1, marginY = (by1 - by0) * 0.1;
    const align = ["left", "center", "right"].includes(o.align) ? o.align : "center";
    let cx = align === "left" ? bx0 + marginX : align === "right" ? bx1 - marginX : (bx0 + bx1) / 2;
    let top = o.vertical === "top" ? by0 + marginY : o.vertical === "bottom" ? by1 - marginY - hAll * s : (by0 + by1) / 2 - hAll * s / 2;
    let layout = null;
    if (o.position) {
      const left = Math.max(...ms.map(m => m.actualBoundingBoxLeft || m.width / 2));
      const right = Math.max(...ms.map(m => m.actualBoundingBoxRight || m.width / 2));
      // Fit real ink, including script overhangs, inside the same safe area.
      const inkScale = Math.min(s, (bx1-bx0)*fill/Math.max(left+right,1));
      const width=(left+right)*inkScale, height=hAll*inkScale;
      const safe=[bx0+marginX,by0+marginY,bx1-marginX,by1-marginY];
      const clamp=(v,lo,hi)=>Math.max(lo,Math.min(hi,v));
      const x=clamp(bx0+(bx1-bx0)*o.position.x,safe[0]+width/2,safe[2]-width/2);
      const y=clamp(by0+(by1-by0)*o.position.y,safe[1]+height/2,safe[3]-height/2);
      layout={bounds:[x-width/2,y-height/2,x+width/2,y+height/2],safe,bbox:rec.bbox,
        position:{x:(x-bx0)/(bx1-bx0),y:(y-by0)/(by1-by0)},scale:inkScale};
      cx=x+(left-right)*inkScale/2; top=y-height/2;
    }
    const place = (ctx, colour) => {
      const scale=layout?layout.scale:s;
      ctx.font = `${f.weight} ${100 * scale}px "${f.family}"`; ctx.textAlign = layout?"center":align; ctx.textBaseline = "alphabetic";
      ctx.fillStyle = colour;
      lines.forEach((t, i) => ctx.fillText(t, cx, top + (asc + i * (lineH + gap)) * scale));
    };
    place(mx, "#fff");
    // The exact same mask can be handed to production review; no second layout calculation.
    const withLayout = canvas => { if(layout)canvas.vldTextLayout=layout; return canvas; };
    if (o.artworkOnly) return withLayout(mask);
    try {
      return withLayout(burn(out, mask, rec));
    } catch (e) {
      // tainted canvas (no CORS on the photo): plain ink, still the right place and size
      const k = rec.burn || [0.35, 0.35, 0.35];
      place(octx, `rgba(${Math.round(60 * k[0])},${Math.round(60 * k[1])},${Math.round(60 * k[2])},0.82)`);
      return withLayout(out);
    }
  }

  async function mount(rootEl, opts) {
    const hub = (opts.hub || "").replace(/\/$/, "");
    // everything the editor reads is under the hub's public /pub routes (public_routes.py)
    const fontsJson = hub + "/pub/fonts.json";
    const fontsBase = hub + "/pub/fonts/";
    const designBase = id => opts.designUrl ? opts.designUrl(id) : `${hub}/pub/designs/${id}`;
    const plateUrl = key => `${hub}/pub/plate/` + key.split("/").map(encodeURIComponent).join("/");
    const [cat, recipes] = await Promise.all([VLD.fonts(fontsJson), fetch(hub + "/pub/recipes.json").then(r => r.json())]);
    if (!document.getElementById("vld-fonts-css")) {
      const l = h("link", {rel: "stylesheet", href: hub + "/pub/fonts.css", id: "vld-fonts-css"});
      document.head.appendChild(l);
    }
    const fontLink = document.getElementById("vld-fonts-css");
    const fontStylesReady = fontLink.sheet ? Promise.resolve() : new Promise((resolve, reject) => {
      fontLink.addEventListener("load", resolve, {once: true});
      fontLink.addEventListener("error", () => reject(new Error("Design fonts could not load")), {once: true});
    });
    async function readyDesignFonts(design, overrides) {
      await fontStylesReady;
      await Promise.all(design.layers.filter(L => L.kind === "text" && !L.hidden && !(overrides[L.id] || {}).hidden).flatMap(L => {
        const o = overrides[L.id] || {}, specs = o.font ? [o.font] : [L.font, ...(L.lines || []).map(line => line.font)].filter(Boolean);
        return (specs.length ? specs : ["great-vibes"]).map(async spec => {
          const font = VLD.resolveFont(spec);
          const loaded = await document.fonts.load(`${o.weight || font.weight} 100px "${font.family}"`, String(o.text ?? L.text ?? "").trim() || "Aa");
          if (!loaded.length) throw new Error("The design font could not load");
        });
      }));
    }
    const st = {design: null, overrides: {layers: {}}, base: String(opts.base || "34"), backdrop: null, colour: null,
                warnings: {}, plateCache: {}};
    const bases = recipes.bases[st.base];
    st.backdrop = opts.backdrop && bases[opts.backdrop] ? opts.backdrop : Object.keys(bases)[0];
    st.colour = opts.colour || (bases[st.backdrop].Marigold ? "Marigold" : Object.keys(bases[st.backdrop])[0]);

    rootEl.classList.add("vld-ed");
    rootEl.innerHTML = `
      <div class="vld-ed__stage"><canvas class="vld-ed__product"></canvas><div class="vld-ed__busy">Engraving...</div></div>
      <div class="vld-ed__colours"></div>
      <div class="vld-ed__art"><svg class="vld-ed__svg"></svg><div class="vld-ed__hint">Drag text to move it</div></div>
      <div class="vld-ed__fields"></div>
      <div class="vld-ed__actions"><button type="button" class="vld-ed__add">+ Add a line of text</button>
        <button type="button" class="vld-ed__reset">Reset</button></div>`;
    const $ = s => rootEl.querySelector(s);
    const svg = $(".vld-ed__svg"), canvas = $(".vld-ed__product");
    let timer = null, drawing = 0;

    function allowsText() { return !opts.customisation || ["Text", "Both"].includes(opts.customisation.mode); }
    function editableText(l) { return allowsText() && l.kind === "text" && !l.hidden && (!opts.customisation || l.editable || l.added); }
    function textLayers() { return st.design.layers.filter(editableText); }
    function textLabel(L, fallback) { return opts.customisation?.field_roles?.[L.id]?.label || (L.added ? "Your text" : fallback); }
    function ov(id) { return st.overrides.layers[id] = st.overrides.layers[id] || {}; }

    // ----- font picker: a scroll-snapping row of chips, each showing the customer's word in that font -----
    const CATS = ["script", "brush", "serif", "sans", "hand"];           // display order; anything else follows
    const catLabel = c => ({script: "Script", brush: "Brush", serif: "Serif", sans: "Sans", hand: "Hand", mono: "Mono", symbols: "Symbols", initials: "Initials"}[c] || c);
    // Customers see a curated list; the hub Studio passes {allFonts: true} for the whole catalogue. Hidden: the
    // mono / symbols categories and the workhorse fonts that only exist to replace old PSD fonts (fonts.json psd_map).
    const HIDE_CATS = ["mono", "symbols"];
    const HIDE_IDS = ["courier-prime", "noto-sans-symbols-2", "caladea", "montserrat", "anton", "manjari", "bubbler-one", "chathura",
      "sofia-sans-extra-condensed", "gantari", "kumbh-sans", "urbanist", "manrope", "encode-sans-condensed-xl", "advent-pro",
      "libre-baskerville", "eb-garamond", "gelasio", "patrick-hand", "gochi-hand"];
    const SANS_KEEP = ["cinzel"];                                         // the only sans a customer may pick (if the catalogue files it there)
    function fontGroups(selected) {
      // [[category, [[id, font], ...]], ...]: the design's current font first, then alphabetical; empty groups dropped
      // 19 Oct 2026: "initials" fonts (decorative alphabets drawn as artwork) are offered only on a layer that already
      // uses one: they are made for one or two letters, not for names or messages
      const groups = {};
      const selInitials = !!(selected && cat.fonts[selected] && cat.fonts[selected].category === "initials");
      Object.entries(cat.fonts).forEach(([id, f]) => {
        const c = f.category || "other";
        if (c === "initials" && !selInitials && !opts.allFonts) return;
        if (!opts.allFonts) {
          if (HIDE_CATS.indexOf(c) >= 0 || HIDE_IDS.indexOf(id) >= 0) return;
          if (c === "sans" && SANS_KEEP.indexOf(id) < 0) return;
        }
        (groups[c] = groups[c] || []).push([id, f]);
      });
      if (selected && cat.fonts[selected] && !Object.values(groups).some(xs => xs.some(([id]) => id === selected))) {
        const f = cat.fonts[selected];                                    // a hidden font the design itself uses stays pickable
        (groups[f.category || "other"] = groups[f.category || "other"] || []).push([selected, f]);
      }
      let order = CATS.filter(c => groups[c]).concat(Object.keys(groups).filter(c => CATS.indexOf(c) < 0));
      if (selInitials) order = ["initials"].concat(order.filter(c => c !== "initials"));   // an initials layer: its alphabets first
      return order.map(c => [c, groups[c].sort((a, b) => (a[0] === selected ? -1 : b[0] === selected ? 1 : a[1].name.localeCompare(b[1].name)))]);
    }
    // fonts are 100-400 KB each: a chip only takes its font once it is scrolled near (font-display: swap does the rest)
    const hasIO = "IntersectionObserver" in window;
    function observeChips(scroller) {
      if (!hasIO) { scroller.querySelectorAll(".vld-ed__chip").forEach(c => { c.style.fontFamily = `'${c.dataset.family}'`; }); return; }
      const io = new IntersectionObserver(entries => entries.forEach(en => {
        if (!en.isIntersecting) return;
        en.target.style.fontFamily = `'${en.target.dataset.family}'`;
        io.unobserve(en.target);
      }), {root: scroller, rootMargin: "0px 320px 0px 320px"});
      scroller.querySelectorAll(".vld-ed__chip").forEach(c => io.observe(c));
    }

    function sampleFor(L) {
      // the layer's current first word, if it is shorter than the font name, else the name; 10 characters at most
      const o = st.overrides.layers[L.id] || {};
      const word = String(o.text ?? L.text ?? "").trim().split(/\s+/)[0] || "";
      const up = (o.case || L.case) === "upper";
      return (name) => {
        const w = up ? word.toUpperCase() : word;
        return ((w && w.length <= name.length) ? w : name).slice(0, 10);
      };
    }

    function fontChips(L, selected) {
      const sample = sampleFor(L);
      const label = textLabel(L, L.name || "text");
      const box = h("div", {class: "vld-ed__fonts", role: "radiogroup", "aria-label": `Font for ${label}`});
      fontGroups(selected).forEach(([c, xs]) => {
        const g = h("div", {class: "vld-ed__fontgroup"}, `<span class="vld-ed__fontcat" aria-hidden="true">${esc(catLabel(c))}</span>`);
        const row = h("div", {class: "vld-ed__chips"});
        xs.forEach(([id, f]) => {
          const on = id === selected;
          const b = h("button", {type: "button", role: "radio", class: "vld-ed__chip" + (on ? " is-on" : "") + (sample(f.name).length > 6 ? " is-long" : ""), "data-font": id,
            "data-family": "vld-" + id, "aria-checked": on ? "true" : "false", tabindex: on ? "0" : "-1", "aria-label": f.name, title: f.name},
            `<span class="vld-ed__chip-sample">${esc(sample(f.name))}</span><span class="vld-ed__chip-name">${esc(f.name)}</span>`);
          if (on) b.style.fontFamily = `'vld-${id}'`;
          if (f.weight) b.style.fontWeight = f.weight;
          b.addEventListener("click", () => pick(id, true));
          row.appendChild(b);
        });
        g.appendChild(row); box.appendChild(g);
      });
      function chips() { return [...box.querySelectorAll(".vld-ed__chip")]; }
      function reveal(chip, smooth) {
        const r = chip.getBoundingClientRect(), s = box.getBoundingClientRect();
        if (!s.width) return;
        const left = box.scrollLeft + (r.left - s.left) - (s.width - r.width) / 2;
        try { box.scrollTo({left: Math.max(0, left), behavior: smooth ? "smooth" : "auto"}); } catch (e) { box.scrollLeft = Math.max(0, left); }
      }
      function pick(id, focus) {
        chips().forEach(c => {
          const on = c.dataset.font === id;
          c.classList.toggle("is-on", on); c.setAttribute("aria-checked", on ? "true" : "false"); c.tabIndex = on ? 0 : -1;
          if (on) { c.style.fontFamily = `'${c.dataset.family}'`; if (focus) c.focus({preventScroll: true}); reveal(c, true); }
        });
        if (ov(L.id).font !== id) { ov(L.id).font = id; schedule(); }
      }
      box.addEventListener("keydown", e => {
        const all = chips(), i = all.findIndex(c => c.getAttribute("aria-checked") === "true");
        let j = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") j = (i + 1) % all.length;
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp") j = (i - 1 + all.length) % all.length;
        else if (e.key === "Home") j = 0; else if (e.key === "End") j = all.length - 1;
        if (j === null) return;
        e.preventDefault(); pick(all[j].dataset.font, true);
      });
      box.refreshSamples = () => {
        const s = sampleFor(L);
        chips().forEach(c => { const t = s(c.getAttribute("aria-label")); c.querySelector(".vld-ed__chip-sample").textContent = t; c.classList.toggle("is-long", t.length > 6); });
      };
      box.centre = () => { const c = box.querySelector(".vld-ed__chip.is-on"); if (c) reveal(c, false); };
      observeChips(box);
      return box;
    }

    function buildFields() {
      const box = $(".vld-ed__fields");
      box.innerHTML = "";
      $(".vld-ed__add").hidden = !allowsText();
      textLayers().forEach((L, i) => {
        const o = st.overrides.layers[L.id] || {};
        const cur = (o.font || L.font || "").split(":")[0];
        const label = textLabel(L, L.name && L.name.length < 30 ? L.name : `Text ${i + 1}`);
        const f = h("div", {class: "vld-ed__field", "data-id": L.id}, `
          <label>${esc(label)}</label>
          <textarea rows="${Math.min(4, (String(o.text ?? L.text).match(/\n/g) || []).length + 1)}" aria-label="${esc(label)}">${esc(o.text ?? L.text)}</textarea>
          <span class="vld-ed__sub">Font</span>
          <div class="vld-ed__row"><span class="vld-ed__sub">Size</span>
            <input class="vld-ed__size" type="range" min="40" max="160" value="${Math.round((o.scale || 1) * 100)}" aria-label="Size of ${esc(label)}"></div>
          <div class="vld-ed__warn" hidden>This is long: it will engrave small. A shorter version will look better.</div>`);
        const chips = fontChips(L, cur);
        f.insertBefore(chips, f.querySelector(".vld-ed__row"));
        f.querySelector("textarea").addEventListener("input", e => {
          ov(L.id).text = e.target.value;
          if (L.derive) (st.derivedManual = st.derivedManual || {})[L.id] = true;   // the customer chose their own initial
          followDerived(L.id, e.target.value); chips.refreshSamples(); schedule(); });
        f.querySelector(".vld-ed__size").addEventListener("input", e => { ov(L.id).scale = e.target.value / 100; schedule(); });
        if (L.added) {
          const remove = h("button", {type: "button", class: "vld-ed__remove", "aria-label": "Remove this extra line of text"}, "Remove line");
          remove.addEventListener("click", () => {
            st.design.layers = st.design.layers.filter(layer => layer.id !== L.id);
            delete st.overrides.layers[L.id];
            buildFields(); schedule(0);
            const inputs = rootEl.querySelectorAll('.vld-ed__field textarea');
            (inputs[Math.min(i, inputs.length - 1)] || $(".vld-ed__add")).focus({preventScroll: true});
          });
          f.appendChild(remove);
        }
        box.appendChild(f);
        requestAnimationFrame(chips.centre);
      });
      // map layers (3 Nov 2026): the customer types a place, picks it, and the street map is drawn for it by the hub
      // (/pub/map/art.png, the same picture the order is engraved from). Text layers that derive from the map
      // ({"derive": {"from": <map layer>, "take": "coords" | "place"}}) fill in from the chosen place.
      st.design.layers.filter(l => l.kind === "image" && l.map && !l.hidden).forEach(L => {
        if (!document.getElementById("vld-map-style")) {
          const css = document.createElement("style"); css.id = "vld-map-style";
          css.textContent = ".vld-map__results{display:grid;gap:4px;margin-top:6px}.vld-map__results button{text-align:left;padding:9px 11px;border:1px solid #ddd;border-radius:8px;background:#fff;cursor:pointer;font:inherit}.vld-map__results button small{display:block;color:#777}.vld-map__zoom{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}.vld-map__zoom button{padding:5px 11px;border:1px solid #ccc;border-radius:99px;background:#fff;cursor:pointer;font:inherit;font-size:13px}.vld-map__zoom button[aria-pressed=true]{background:#111;color:#fff;border-color:#111}.vld-map__status{color:#666;font-size:13px;margin:6px 0 0}";
          document.head.appendChild(css);
        }
        const chosen = () => ov(L.id).map;
        const f = h("div", {class: "vld-ed__field vld-map", "data-id": L.id},
          `<label for="map-${esc(L.id)}">${esc(L.map.label || "Your place")}</label>
           <input id="map-${esc(L.id)}" type="search" autocomplete="off" placeholder="Type a venue, town or postcode" value="${esc(chosen()?.label || "")}">
           <div class="vld-map__results" role="listbox"></div>
           <div class="vld-map__zoom" role="group" aria-label="Map zoom">${[["street", "Street"], ["neighbourhood", "Neighbourhood"], ["town", "Town"], ["city", "City"]]
             .map(([k, l]) => `<button type="button" data-z="${k}" aria-pressed="${(chosen()?.zoom || L.map.zoom || "neighbourhood") === k}">${l}</button>`).join("")}</div>
           <p class="vld-map__status" role="status">${chosen() ? "" : "Type where it happened, then pick it from the list."}</p>`);
        const input = f.querySelector("input"), results = f.querySelector(".vld-map__results"), status = f.querySelector(".vld-map__status");
        let timer = null, req = 0;
        const apply = () => {
          const m = chosen(); if (!m) return;
          const u = `${hub}/pub/map/art.png?lon=${m.lon}&lat=${m.lat}&zoom=${m.zoom}&shape=${encodeURIComponent(L.map.shape || "circle")}&size=1000`;
          ov(L.id).image = {src: u, width: 1000, height: 1000, name: m.label};
          st.design.layers.forEach(D => {
            const d = D.derive; if (!d || String(d.from) !== String(L.id)) return;
            const t = d.take === "coords" ? m.coords : d.take === "place" ? m.label : null; if (!t) return;
            ov(D.id).text = t; const ta = rootEl.querySelector('.vld-ed__field[data-id="' + D.id + '"] textarea'); if (ta) ta.value = t;
          });
          status.textContent = "Map drawn for " + m.label + ".";
          schedule(0);
        };
        input.addEventListener("input", () => {
          clearTimeout(timer); const q = input.value.trim(); results.innerHTML = "";
          if (q.length < 2) return;
          timer = setTimeout(async () => {
            const n = ++req; status.textContent = "Searching…";
            try {
              const r = await fetch(`${hub}/pub/map/search?q=${encodeURIComponent(q)}`).then(r => r.json());
              if (n !== req) return;
              const list = r.results || [];
              status.textContent = list.length ? "Pick the right place:" : (r.error || "No places found. Try a town or postcode.");
              results.innerHTML = list.map((p, i) => `<button type="button" role="option" data-i="${i}">${esc(p.name)}<small>${esc(p.place)}</small></button>`).join("");
              results.querySelectorAll("button").forEach(b => b.addEventListener("click", () => {
                const p = list[+b.dataset.i];
                ov(L.id).map = {lon: p.lon, lat: p.lat, zoom: chosen()?.zoom || L.map._zoom || L.map.zoom || "neighbourhood", label: p.name, coords: p.coords};
                input.value = p.name; results.innerHTML = ""; apply();
              }));
            } catch (e) { if (n === req) status.textContent = "Place search is unavailable. Please try again."; }
          }, 350);
        });
        f.querySelectorAll(".vld-map__zoom button").forEach(b => b.addEventListener("click", () => {
          f.querySelectorAll(".vld-map__zoom button").forEach(x => x.setAttribute("aria-pressed", String(x === b)));
          if (chosen()) { chosen().zoom = b.dataset.z; apply(); } else { (L.map._zoom = b.dataset.z); }
        }));
        box.appendChild(f);
      });
      // the artwork (icons, flowers, bars): drag it on the preview; here: size and show/hide. Nothing is deleted
      // from the design itself, so Reset brings everything back.
      const art = st.design.layers.filter(l => l.kind === "image" && !l.hidden && !l.locked && !l.map);   // locked: part of the composition (e.g. the band a split monogram sits behind)
      if (art.length) {
        const wrap = h("div", {class: "vld-ed__arts"}, art.length > 1 ? `<span class="vld-ed__sub">Artwork</span>` : "");   // not vld-ed__art: that is the preview box. One piece: no group heading, the row says "Artwork"
        // labels: one piece -> "Artwork"; Photoshop shape/layer names and icon ids -> "Artwork N"; repeats numbered
        const PSD = /^(ellipse|rectangle|rounded rectangle|shape|layer|group|vector|path|polygon|line|smart object)\b/i;
        const raw = art.map((L, i) => art.length === 1 ? "Artwork"
          : (L.name && L.name.length < 30 && !L.name.includes(":") && !PSD.test(L.name) ? L.name.replace(/\s+\d+$/, "") : `Artwork ${i + 1}`));
        const seen = {};
        const artLabels = raw.map(n => {
          const total = raw.filter(x => x === n).length;
          if (total < 2) return n;
          seen[n] = (seen[n] || 0) + 1;
          return `${n} ${seen[n]}`;
        });
        art.forEach((L, i) => {
          const o = st.overrides.layers[L.id] || {};
          const label = artLabels[i];
          const row = h("div", {class: "vld-ed__field vld-ed__field--art", "data-id": L.id}, `
            <div class="vld-ed__row"><label class="vld-ed__artname">${esc(label)}</label>
              <label class="vld-ed__toggle"><input type="checkbox" class="vld-ed__show" ${o.hidden ? "" : "checked"} aria-label="Show ${esc(label)}"> Show</label></div>
            <div class="vld-ed__row"><span class="vld-ed__sub">Size</span>
              <input class="vld-ed__size" type="range" min="40" max="160" value="${Math.round((o.scale || 1) * 100)}" aria-label="Size of ${esc(label)}" ${o.hidden ? "disabled" : ""}></div>`);
          row.querySelector(".vld-ed__size").addEventListener("input", e => { ov(L.id).scale = e.target.value / 100; schedule(); });
          row.querySelector(".vld-ed__show").addEventListener("change", e => {
            ov(L.id).hidden = !e.target.checked; row.querySelector(".vld-ed__size").disabled = !e.target.checked; schedule(0);
          });
          if (["Image", "Both"].includes(opts.customisation?.mode) && opts.customisation?.prepareEndpoint) {
            const upload=h("div",{class:"vld-image-upload"},`<label>Your image<input type="file" accept="image/png,image/jpeg,image/webp" aria-label="Choose your image"></label><p>Choose a clear image. PNG, JPG or WebP, up to 10 MB.</p><p class="vld-image-status" role="status"></p><button type="button" class="vld-image-remove" hidden>Remove your image</button>`);
            const input=upload.querySelector('input'),status=upload.querySelector('[role=status]'),remove=upload.querySelector('button');
            const detailBox=h('div',{class:'vld-image-detail',hidden:true},`<label>Line detail <output>Balanced</output><input type="range" min="0" max="100" step="25" value="50" aria-label="Line detail"></label><div class="vld-ed__row"><span>Cleaner outlines</span><span>Finer detail</span></div><p>Slide to keep the details you love and reduce unwanted lines.</p>`);
            remove.before(detailBox);
            const detailSlider=detailBox.querySelector('input'),detailOutput=detailBox.querySelector('output');
            let request=0,variants=st.imageVariants?.[L.id]||[];
            detailBox.hidden=!variants.length;detailSlider.value=ov(L.id).image?.detail??50;detailOutput.textContent=['Cleanest','Cleaner','Balanced','Detailed','Finest'][Number(detailSlider.value)/25];
            detailSlider.addEventListener('input',()=>{
              const variant=variants.find(v=>v.detail===Number(detailSlider.value));
              if(!variant||!ov(L.id).image)return;
              Object.assign(ov(L.id).image,{src:variant.src,detail:variant.detail,checks:variant.checks});
              if(ov(L.id).image.asset)Object.assign(ov(L.id).image.asset,{detail:variant.detail,sha256:variant.sha256});
              detailOutput.textContent=['Cleanest','Cleaner','Balanced','Detailed','Finest'][variant.detail/25];
              schedule(0);
            });
            function refreshUpload(){const image=ov(L.id).image;remove.hidden=!image;status.textContent=image?`${image.name}. Background removed and converted to line art. Check that the outline and details match your photo.`:'Choose your own image to replace this artwork.';}
            refreshUpload();
            input.addEventListener('change',async()=>{
              const file=input.files[0];if(!file)return;
              const n=++request,design=st.design;
              if(!['image/png','image/jpeg','image/webp'].includes(file.type)||file.size>10*1024*1024){status.textContent='Choose a PNG, JPG or WebP under 10 MB.';ov(L.id).imagePending=false;ov(L.id).imageError=status.textContent;remove.hidden=false;input.value='';schedule(0);return;}
              detailSlider.disabled=true;
              ov(L.id).imagePending=true;delete ov(L.id).imageError;
              status.textContent='Removing the background and drawing the details…';
              schedule(0);
              try {
                if(!opts.customisation.prepareEndpoint)throw Error('Image preparation is not connected yet.');
                const response=await fetch(opts.customisation.prepareEndpoint,{method:'POST',headers:{'Content-Type':file.type},body:file,signal:AbortSignal.timeout(90000)});
                const envelope=await response.json();let prepared=envelope.data||envelope;
                if(!response.ok)throw Error(prepared.error||'We could not isolate the subject. Try another photo.');
                if(response.status===202&&prepared.job_id&&prepared.token){
                  const job=prepared,deadline=Date.now()+120000;
                  const statusUrl=new URL('../'+encodeURIComponent(job.job_id),opts.customisation.prepareEndpoint).href;
                  while(Date.now()<deadline){
                    if(n!==request||st.design!==design)return;
                    await new Promise(resolve=>setTimeout(resolve,1500));
                    const poll=await fetch(statusUrl,{headers:{'X-Upload-Token':job.token},signal:AbortSignal.timeout(15000)});
                    const state=await poll.json();
                    if(!poll.ok||state.status==='failed')throw Error(state.error||'Your photo could not be prepared. Please try again.');
                    if(state.status==='ready'){prepared=state.prepared;break;}
                    status.textContent=state.status==='running'?'Removing the background and drawing the details…':'Your photo is queued. Preparing it shortly…';
                  }
                  if(!prepared.src)throw Error('Photo preparation is taking longer than expected. Please try again shortly.');
                }
                if(!/^data:image\/png;base64,/.test(prepared.src)||!prepared.width||!prepared.height)throw Error('The prepared artwork is invalid.');
                if(n!==request||st.design!==design)return;
                variants=prepared.variants||[];(st.imageVariants||={})[L.id]=variants;detailBox.hidden=!variants.length;detailSlider.value=prepared.detail??50;detailOutput.textContent='Balanced';detailSlider.disabled=false;
                ov(L.id).image={src:prepared.src,name:file.name,width:prepared.width,height:prepared.height,status:prepared.status,method:prepared.method,checks:prepared.checks,detail:prepared.detail??50};
                if(prepared.asset){const chosen=variants.find(v=>v.detail===(prepared.detail??50));ov(L.id).image.asset={...prepared.asset,detail:prepared.detail??50,sha256:chosen?.sha256};}
                ov(L.id).hidden=false;row.querySelector('.vld-ed__show').checked=true;row.querySelector('.vld-ed__size').disabled=false;
                refreshUpload();schedule(0);
              }catch(e){if(n===request&&st.design===design){ov(L.id).imageError='Your new image could not be prepared. Choose another image or remove it.';status.textContent=e instanceof TypeError?'Image preparation is unavailable. Please try again.':e.message;remove.hidden=false;}}finally{if(n===request&&st.design===design){ov(L.id).imagePending=false;input.value='';schedule(0);}}
            });
            remove.addEventListener('click',()=>{request++;variants=[];delete st.imageVariants?.[L.id];detailBox.hidden=true;delete ov(L.id).image;delete ov(L.id).imageError;ov(L.id).imagePending=false;input.value='';refreshUpload();schedule(0);});
            row.prepend(upload);
          }
          wrap.appendChild(row);
        });
        wrap.appendChild(h("span", {class: "vld-ed__arthint"}, art.length > 1 ? "Drag any of them on the preview to move it." : "Drag it on the preview to move it."));
        box.appendChild(wrap);
      }
    }

    function buildColours() {
      const cols = Object.keys(recipes.bases[st.base][st.backdrop]).sort();
      $(".vld-ed__colours").innerHTML = "";
      cols.forEach(c => {
        const b = h("button", {type: "button", class: c === st.colour ? "on" : ""}, esc(c));
        b.onclick = () => { st.colour = c; buildColours(); schedule(0); if (opts.onColour) opts.onColour(c); };
        $(".vld-ed__colours").appendChild(b);
      });
    }

    function effectiveDesign() {
      // apply size (scale) and drag (dx, dy) overrides to the layer boxes; text/font go through VLD.render
      const d = JSON.parse(JSON.stringify(st.design));
      d._base = st.design._base;
      d.layers.forEach(L => {
        const o = st.overrides.layers[L.id];
        if (!o || (L.kind !== "text" && L.kind !== "image")) return;
        if (L.kind === "image" && o.image) {
          L.src=o.image.src;delete L.svg;delete L.quad;
          const [a,b,c,d]=L.box,ratio=o.image.width/o.image.height;
          const w=Math.min(c-a,(d-b)*ratio),h=w/ratio;
          L.box=[(a+c-w)/2,(b+d-h)/2,(a+c+w)/2,(b+d+h)/2];
        }
        let [x0, y0, x1, y1] = L.box;
        const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, s = o.scale && o.scale !== 1 ? o.scale : 1, dx = o.dx || 0, dy = o.dy || 0;
        if (s !== 1) {
          const w = (x1 - x0) * s / 2, hh = (y1 - y0) * s / 2;
          [x0, y0, x1, y1] = [cx - w, cy - hh, cx + w, cy + hh];
        }
        L.box = [x0 + dx, y0 + dy, x1 + dx, y1 + dy];
        // vector art placed by a quad moves and scales about the same centre (mirrors the hub's _apply_box_overrides)
        if (L.quad) L.quad = L.quad.map(([qx, qy]) => [cx + (qx - cx) * s + dx, cy + (qy - cy) * s + dy]);
      });
      return d;
    }

    function plate(key) {
      return st.plateCache[key] = st.plateCache[key] || loadImage(plateUrl(key));
    }

    async function draw(quiet) {
      const n = ++drawing;
      if (!quiet) rootEl.classList.add("is-busy");
      await readyDesignFonts(st.design, st.overrides.layers);
      if (n !== drawing) return;
      const report = await VLD.render(svg, effectiveDesign(), {layers: st.overrides.layers}, {});
      st.warnings = report;
      rootEl.querySelectorAll(".vld-ed__field").forEach(f => {
        const r = report[f.dataset.id], warn = f.querySelector(".vld-ed__warn");
        if (warn) warn.hidden = !(r && r.tooLong);   // artwork rows have no length warning
      });
      enableDrag();
      const rec = recipes.bases[st.base][st.backdrop][st.colour];
      const [pl, svgString] = await Promise.all([plate(rec.plate), selfContainedSvg(svg, fontsJson, fontsBase)]);
      if (n !== drawing) return;
      const {mask, fit} = await artworkMask(svgString, pl.naturalWidth, pl.naturalHeight, rec, opts.fill || 0.86, st.referenceBounds);
      const out = burn(pl, mask, rec);
      if (n !== drawing) return;
      st.fit = fit;
      canvas.width = out.width; canvas.height = out.height;
      canvas.getContext("2d").drawImage(out, 0, 0);
      st.lastProduct = out;
      rootEl.classList.remove("is-busy");
      if (opts.onChange) opts.onChange(api.state());
      return true;
    }

    function schedule(ms) { clearTimeout(timer); timer = setTimeout(draw, ms === undefined ? 250 : ms); }
    // live drag (Matt 2 Nov 2026: "allow live movement of the drag not drag drop then it teleports"): redraw as
    // often as a render finishes instead of waiting for the finger to stop. One render in flight; the newest
    // position is drawn next. The debounced schedule() kept restarting on every pointermove, so nothing drew.
    let liveInflight = null, liveAgain = false;
    function liveDraw() {
      if (liveInflight) { liveAgain = true; return; }
      clearTimeout(timer);
      liveInflight = Promise.resolve(draw(true)).catch(() => {}).finally(() => {
        liveInflight = null;
        if (liveAgain) { liveAgain = false; liveDraw(); }
      });
    }

    // derived layers (3 Nov 2026, design 109 split monogram): {"derive": {"from": <field>, "take": "initial"}} follows the
    // first letter of that field until the customer types their own letter
    function followDerived(srcId, value) {
      st.design.layers.forEach(D => {
        const d = D.derive;
        if (!d || String(d.from) !== String(srcId) || d.take !== "initial" || (st.derivedManual || {})[D.id]) return;
        const m = String(value || "").match(/\p{L}/u);
        if (!m) return;
        const ch = m[0].toLocaleUpperCase();
        ov(D.id).text = ch;
        const ta = rootEl.querySelector('.vld-ed__field[data-id="' + D.id + '"] textarea');
        if (ta) ta.value = ch;
      });
    }

    function enableDrag() {
      svg.querySelectorAll("g[data-layer]").forEach(g => {
        const layer=st.design.layers.find(L=>String(L.id)===g.getAttribute("data-layer"));
        if((layer?.kind==='text'&&!editableText(layer))||layer?.locked){g.style.cursor='default';g.onpointerdown=null;return;}
        g.style.cursor = "move";
        g.onpointerdown = e => {
          const id = g.getAttribute("data-layer"), o = ov(id);
          const pt = svg.createSVGPoint(), m = svg.getScreenCTM().inverse();
          pt.x = e.clientX; pt.y = e.clientY;
          const start = pt.matrixTransform(m), sx = o.dx || 0, sy = o.dy || 0;
          const downX = e.clientX, downY = e.clientY;
          let moved = false;
          g.setPointerCapture(e.pointerId);
          g.onpointermove = ev => {
            if (!moved && Math.hypot(ev.clientX - downX, ev.clientY - downY) < 6) return;   // a tap, not a drag, so far
            moved = true;
            pt.x = ev.clientX; pt.y = ev.clientY;
            const p = pt.matrixTransform(m);
            o.dx = sx + p.x - start.x; o.dy = sy + p.y - start.y;
            if (g.dataset.base === undefined) g.dataset.base = g.getAttribute("transform") || "";
            g.setAttribute("transform", `translate(${p.x - start.x} ${p.y - start.y}) ` + g.dataset.base);
          };
          g.onpointerup = () => {
            g.onpointermove = null; delete g.dataset.base;
            if (moved) schedule(0); else focusLayer(id);   // tap on a word: go to its field
          };
        };
      });
    }

    function focusLayer(id) {
      const f = rootEl.querySelector(`.vld-ed__field[data-id="${CSS.escape ? CSS.escape(id) : id}"]`);
      if (!f) return false;
      const ta = f.querySelector("textarea") || f.querySelector(".vld-ed__size");
      try { f.scrollIntoView({block: "center", behavior: "smooth"}); } catch (e) { f.scrollIntoView(); }
      try { ta.focus({preventScroll: true}); } catch (e) { ta.focus(); }
      if (ta.setSelectionRange) ta.setSelectionRange(ta.value.length, ta.value.length);
      f.classList.add("is-hit"); setTimeout(() => f.classList.remove("is-hit"), 900);
      return true;
    }

    function layerAt(fx, fy) {
      // fx, fy: fractions (0..1) of the engraved product canvas -> the text layer under that point, or null
      const fit = st.fit;
      if (!fit || !st.design) return null;
      const ax = fit.x0 + (fx * fit.W - fit.dx) / fit.s, ay = fit.y0 + (fy * fit.H - fit.dy) / fit.s;
      const [W, H] = st.design.canvas;
      let best = null, bestD = Infinity, bestArt = null, bestArtD = Infinity;
      effectiveDesign().layers.forEach(L => {
        const o = st.overrides.layers[L.id] || {};
        if (L.hidden || o.hidden || (L.kind !== "text" && L.kind !== "image")) return;
        if (L.kind === "text" && !editableText(L)) return;
        if (L.locked) return;
        const [x0, y0, x1, y1] = L.box, px = L.kind === "text" ? (x1 - x0) * 0.15 + W * 0.02 : 0, py = L.kind === "text" ? (y1 - y0) * 0.3 + H * 0.02 : 0;
        const inside = ax >= x0 - px && ax <= x1 + px && ay >= y0 - py && ay <= y1 + py;
        const d = Math.hypot(ax - (x0 + x1) / 2, ay - (y0 + y1) / 2);
        if (!inside) return;
        if (L.kind === "text") { if (d < bestD) { best = L.id; bestD = d; } }
        else if (d < bestArtD) { bestArt = L.id; bestArtD = d; }
      });
      return best || bestArt;   // words win over the art behind them
    }

    async function loadDesign(id) {
      st.design = await VLD.load(designBase(id));
      st.overrides = {layers: {}};
      st.nextAddedId = 1;
      st.imageVariants = {};
      st.referenceBounds = null;
      if (opts.stableLayout) {
        // Establish the frame from the unedited design, before exposing controls.
        // Reset and design changes reproduce it; colour changes reuse it.
        const original = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        await readyDesignFonts(st.design, {});
        await VLD.render(original, st.design, {layers: {}}, {});
        const source = await selfContainedSvg(original, fontsJson, fontsBase);
        const rec = recipes.bases[st.base][st.backdrop][st.colour];
        const pl = await plate(rec.plate);
        const reference = await artworkMask(source, pl.naturalWidth, pl.naturalHeight, rec, opts.fill || 0.86);
        if (!reference.bounds) throw new Error("The design has no printable artwork");
        st.referenceBounds = reference.bounds;
      }
      buildFields(); await draw();
    }

    $(".vld-ed__add").onclick = () => {
      if (!allowsText()) return;
      const [W, H] = st.design.canvas;
      while (st.design.layers.some(layer => layer.id === "add" + st.nextAddedId)) st.nextAddedId++;
      const n = st.nextAddedId++;
      st.design.layers.push({id: "add" + n, name: "Your text", kind: "text", text: "Your text", font: "great-vibes",
        align: "center", colour: "#111111", box: [W * 0.2, H * 0.78, W * 0.8, H * 0.95], editable: true, added: true});
      buildFields(); schedule(0);
    };
    $(".vld-ed__reset").onclick = () => { st.derivedManual = {}; loadDesign(st.design.id); };

    function movementArea(){
      const rec=recipes.bases[st.base][st.backdrop][st.colour],candidate=opts.dragAreas?.[st.backdrop]?.[st.colour];
      if(candidate&&candidate.plate===rec.plate&&candidate.size[0]===st.fit?.W&&candidate.size[1]===st.fit?.H)return candidate.bbox;
      // Other bases stay within their existing recipe, never a guessed photo box.
      return rec.bbox;
    }
    const api = {
      async setDesign(id) { await loadDesign(id); },
      setProduct(p) { Object.assign(st, p); buildColours(); schedule(0); },
      state() {
        return {design_id: st.design.id, design_version: st.design.version_hash || "",
          design_format: st.design.format,
          imagePending: Object.values(st.overrides.layers).some(o=>o.imagePending),
          imageError: Object.values(st.overrides.layers).find(o=>o.imageError)?.imageError,
          imageUploads: Object.values(st.overrides.layers).filter(o=>o.image).map(o=>({name:o.image.name,status:o.image.status})),
          layout: st.referenceBounds ? {version: 1, mode: "fixed-design-bounds", bounds: st.referenceBounds, fill: opts.fill || 0.86, movementArea:movementArea()} : null,
          overrides: st.overrides, added: st.design.layers.filter(l => l.added),
          product: {base: st.base, backdrop: st.backdrop, colour: st.colour},
          warnings: Object.entries(st.warnings).filter(([, r]) => r.tooLong).map(([id]) => id),
          texts: Object.fromEntries(textLayers().map(L => [L.id, (st.overrides.layers[L.id] || {}).text ?? L.text]))};
      },
      async previewBlob() {
        clearTimeout(timer);
        if(!await draw())throw Error('Your design changed while the preview was saving. Please try again.');
        return st.lastProduct.convertToBlob({type: "image/jpeg", quality: 0.9});
      },
      async artworkSvg() {
        const source=await selfContainedSvg(svg, fontsJson, fontsBase),f=st.fit;
        if(!opts.stableLayout||!f)return source;
        const doc=new DOMParser().parseFromString(source,'image/svg+xml'),node=doc.documentElement;
        node.setAttribute('viewBox',[f.x0-f.dx/f.s,f.y0-f.dy/f.s,f.W/f.s,f.H/f.s].join(' '));node.setAttribute('width',f.W);node.setAttribute('height',f.H);
        return new XMLSerializer().serializeToString(node);
      },
      layerAt, focusLayer,
      // drag on the engraved photo (hero / sheet canvas): deltas as fractions of that canvas -> design units
      moveLayer(id, dfx, dfy, done) {
        const fit = st.fit;
        if (!fit || !id) return false;
        let dx=dfx*fit.W/fit.s,dy=dfy*fit.H/fit.s;
        if(opts.stableLayout){
          const area=movementArea(),L=effectiveDesign().layers.find(l=>l.id===id);
          if(!area||!L)return false;
          const b=L.quad?[Math.min(L.box[0],...L.quad.map(p=>p[0])),Math.min(L.box[1],...L.quad.map(p=>p[1])),Math.max(L.box[2],...L.quad.map(p=>p[0])),Math.max(L.box[3],...L.quad.map(p=>p[1]))]:L.box;
          const safe=[fit.x0+(area[0]-fit.dx)/fit.s,fit.y0+(area[1]-fit.dy)/fit.s,fit.x0+(area[2]-fit.dx)/fit.s,fit.y0+(area[3]-fit.dy)/fit.s];
          const clamp=(v,lo,hi)=>lo>hi?0:Math.max(lo,Math.min(hi,v));
          dx=clamp(dx,safe[0]-b[0],safe[2]-b[2]);dy=clamp(dy,safe[1]-b[1],safe[3]-b[3]);
        }
        const o = ov(id);
        o.dx = (o.dx || 0) + dx; o.dy = (o.dy || 0) + dy;
        if (done) { liveAgain = false; schedule(0); } else liveDraw();
        return true;
      },
      colours() { return Object.keys(recipes.bases[st.base][st.backdrop]); },
      recipes() { return recipes; },
      placement() {return {area:movementArea(),fit:st.fit,layers:effectiveDesign().layers};},
      fontOf(id) { const L = textLayers().find(l => l.id === id) || textLayers()[0]; return L ? ((st.overrides.layers[L.id] || {}).font || L.font) : null; },
      fontName(spec) { const f = cat.fonts[String(spec || "").split(":")[0]]; return f ? f.name : spec; },
      messageFonts() { return fontGroups(null).flatMap(([, fonts]) => fonts.map(([id, f]) => ({id, name: f.name}))); },
    };
    buildColours();
    await loadDesign(opts.designId);
    return api;
  }

  root.VLDEditor = {mount, engraveText, loadImage};
})(window);
